import { createContext, useContext, type ReactNode } from 'react';
import { useAuth, type UserProfile } from '@/hooks/useAuth';

export type UserRole = 'driver' | 'fleet_manager' | null;

interface RoleContextValue {
  role: UserRole;
  driverName: string;
  driverUserId: string;
  fleetUser: string;
  authLoading: boolean;
  /** Identifiant de l'utilisateur connecté (sub du JWT), ou null. */
  userId: string | null;
  email: string;
  profile: UserProfile | null;
  /** Compte interne iAUTOLINK : il n'a rien à faire dans l'app. */
  isInternal: boolean;
  logout: () => void;
}

const RoleContext = createContext<RoleContextValue | null>(null);

export function RoleProvider({ children }: { children: ReactNode }) {
  const { userId, email, profile, loading, signOut, isSignedIn } = useAuth();

  /**
   * Le rôle vient de la base, et de nulle part ailleurs.
   *
   * Une ancienne version acceptait un repli sur `user_metadata.role`, une
   * valeur que le navigateur pouvait écrire lui-même à l'inscription : il
   * suffisait de s'inscrire en se déclarant gestionnaire. Ce repli est
   * supprimé.
   *
   * ⚠️ CHANGEMENT : « ceo_admin » n'est PLUS assimilé à « fleet_manager ».
   *
   * Cette assimilation datait de l'époque où il n'y avait qu'une seule
   * application. Depuis que la console interne existe (admin.autolink.lu),
   * elle produisait un bogue visible : un compte interne qui se connecte à
   * l'app atterrissait sur la partie gestionnaire de flotte, sans société,
   * devant des écrans qui n'ont pas de sens pour lui.
   *
   * Un compte interne n'est ni conducteur ni gestionnaire : il n'a pas de
   * place dans l'app. `isInternal` permet à App.tsx de le dire franchement
   * plutôt que de le laisser errer.
   */
  const isInternal = profile?.role === 'ceo_admin';

  const role: UserRole =
    profile?.role === 'fleet_manager'
      ? 'fleet_manager'
      : isSignedIn && !isInternal
      ? 'driver'
      : null;

  const driverName = profile?.full_name || email.split('@')[0] || '';
  const fleetUser = profile?.full_name || 'Gestionnaire de flotte';

  return (
    <RoleContext.Provider
      value={{
        role,
        driverName,
        driverUserId: userId ?? '',
        fleetUser,
        authLoading: loading,
        userId,
        email,
        profile,
        isInternal,
        logout: signOut,
      }}
    >
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useRole must be used within RoleProvider');
  return ctx;
}
